/**
 * @typedef {object} ReviewVm
 * @property {string|null} title
 * @property {string|null} subtitle
 * @property {string|null} destination
 * @property {string|null} dates
 * @property {string|null} nights
 * @property {string|null} passengers
 * @property {{ ils: number|null, usd: number|null }} price
 * @property {{ id: string, label: string }[]} badges
 * @property {{ id: string, title?: string, kind?: string }[]} sections
 * @property {{ id: string, label: string, variant?: string }[]} ctas
 * @property {{ key: string, label: string, valueText: string }[]} [detailRows]
 */

function detailCell(v) {
  if (v == null) return "—";
  const s = String(v).trim();
  return s.length ? s : "—";
}

/**
 * Map public flight-booking / deal context into a review VM (minimal fields).
 *
 * @param {object} raw
 * @param {object} [raw.savedDeal]
 * @param {object} [raw.searchPhrase]
 * @param {Array} [raw.roomsAndPaxInfo]
 * @param {{ priceWithCreditCardFee?: number|string }|null} [raw.ourPrice]
 * @param {(n: number) => number} [raw.ilsToUsd]
 * @param {string|null} [raw.routeDisplay] — adapter-fed label for destination/route row
 * @param {string|null} [raw.datesDisplay] — adapter-fed dates line (defaults from deal)
 * @param {string|null} [raw.passengerSummaryLine] — adapter-fed pax summary (defaults to room count)
 * @param {{ key: string, label: string, valueText: string }[]} [raw.detailRows] — when set, replaces auto rows
 * @returns {ReviewVm}
 */
export function mapFlightToVM(raw = {}) {
  const deal = raw.savedDeal || {};
  const dest =
    deal.arrivalCity ||
    deal.destination ||
    deal.to ||
    (raw.searchPhrase && (raw.searchPhrase.location || raw.searchPhrase.to)) ||
    null;

  const dep = deal.depDate || deal.departureDate || null;
  const ret = deal.retDate || deal.returnDate || null;

  const ilsRaw = raw.ourPrice?.priceWithCreditCardFee ?? deal.totalPrice ?? null;
  const ilsNum = ilsRaw != null && ilsRaw !== "" ? Number(ilsRaw) : null;
  const ils = Number.isFinite(ilsNum) ? ilsNum : null;
  let usd = null;
  if (ils != null && typeof raw.ilsToUsd === "function") {
    const u = Number(raw.ilsToUsd(ils));
    usd = Number.isFinite(u) ? u : null;
  }

  const paxCount = Array.isArray(raw.roomsAndPaxInfo) ? raw.roomsAndPaxInfo.length : 0;
  const datesLine = dep && ret ? `${dep} – ${ret}` : dep || ret || null;

  const detailRows =
    Array.isArray(raw.detailRows) && raw.detailRows.length
      ? raw.detailRows
      : [
          { key: "route", label: "יעד", valueText: detailCell(raw.routeDisplay ?? dest) },
          { key: "dates", label: "תאריכים", valueText: detailCell(raw.datesDisplay ?? datesLine) },
          {
            key: "pax",
            label: "נוסעים",
            valueText: detailCell(
              raw.passengerSummaryLine ?? (paxCount > 0 ? `${paxCount} חדרים` : null)
            ),
          },
        ];

  return {
    title: dest ? `טיסה ל${dest}` : "טיסה",
    subtitle: null,
    destination: dest,
    dates: datesLine,
    nights: null,
    passengers: paxCount > 0 ? String(paxCount) : null,
    price: { ils, usd },
    badges: [],
    sections: [],
    ctas: [],
    detailRows,
  };
}

/**
 * Map flight proposal API row + sidebar helpers into ReviewVm (for summary card / future page shell).
 *
 * @param {object} p
 * @param {object|null} [p.proposal]
 * @param {object|null} [p.offer]
 * @param {string|null} [p.statusLabel]
 * @param {{ lines?: { key: string, label: string, amount?: number|null }[] }} [p.priceSidebar]
 * @returns {ReviewVm}
 */
export function mapFlightProposalToVM(p = {}) {
  const proposal = p.proposal || {};
  const offer = p.offer || {};
  const totalNum = proposal.total_price != null ? Number(proposal.total_price) : null;
  const ils = Number.isFinite(totalNum) ? totalNum : null;

  const lines = Array.isArray(p.priceSidebar?.lines) ? p.priceSidebar.lines : [];

  return {
    title: "סיכום הצעה",
    subtitle: null,
    destination: offer.arrival || offer.destination || null,
    dates:
      offer.dep_date != null || offer.ret_date != null
        ? `${offer.dep_date ?? ""} – ${offer.ret_date ?? ""}`.trim()
        : null,
    nights: null,
    passengers: Array.isArray(offer.passengers_info) ? String(offer.passengers_info.length) : null,
    price: { ils, usd: null },
    badges: p.statusLabel ? [{ id: "status", label: String(p.statusLabel) }] : [],
    sections: [
      {
        id: "price_breakdown",
        kind: "priceLines",
        title: "פירוט",
        lines,
      },
    ],
    ctas: [],
  };
}
